import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Wand2, ArrowRightLeft, Loader2, CheckCircle2, AlertCircle, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import api from '@/lib/api';

/**
 * StripePricingSetupWidget — one-time admin tooling for the 2-tier migration (Feb 2026).
 *
 *  1) Create Thrive Prices  — calls /stripe/admin/create-thrive-prices
 *     Creates the Thrive product + $24.99 Founding + $49.99 Regular prices in Stripe and
 *     persists their IDs in db.system_config.
 *
 *  2) Migrate Legacy Subscribers — calls /stripe/admin/migrate-legacy-subscribers
 *     Moves anyone on a legacy Connect/Thrive price (and without an active free promo) to
 *     the new $24.99 Founding price (proration disabled). Renames DB tier accordingly.
 */
export default function StripePricingSetupWidget({ getAuthHeader }) {
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [creating, setCreating] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastMigration, setLastMigration] = useState(null);
  const [migrationPreview, setMigrationPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPriceType, setBulkPriceType] = useState('founding');
  const [bulkSendEmail, setBulkSendEmail] = useState(true);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);

  const refresh = useCallback(async () => {
    setLoadingConfig(true);
    try {
      const res = await api.get('/stripe/admin/thrive-prices', { headers: getAuthHeader() });
      setConfig(res.data);
    } catch {
      // silent — widget shows "not configured" state
    } finally {
      setLoadingConfig(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await api.post('/stripe/admin/create-thrive-prices', {}, { headers: getAuthHeader() });
      if (res.data.status === 'already_configured') {
        toast.info('Thrive prices were already configured.');
      } else {
        toast.success('Thrive prices created in Stripe.');
      }
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create prices');
    } finally {
      setCreating(false);
    }
  };

  const runPreviewMigration = async () => {
    setPreviewing(true);
    setMigrationPreview(null);
    try {
      const res = await api.post(
        '/stripe/admin/migrate-legacy-subscribers',
        { dry_run: true },
        { headers: getAuthHeader() },
      );
      setMigrationPreview(res.data);
      const s = res.data.summary || {};
      toast.success(`Preview ready — ${s.would_migrate_count || 0} subscription${s.would_migrate_count === 1 ? '' : 's'} would migrate.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const runMigration = async () => {
    setMigrating(true);
    setConfirmOpen(false);
    try {
      const res = await api.post(
        '/stripe/admin/migrate-legacy-subscribers',
        { dry_run: false },
        { headers: getAuthHeader() },
      );
      setLastMigration(res.data);
      setMigrationPreview(null);
      const s = res.data.summary || {};
      toast.success(`Migration complete — ${s.migrated_count || 0} moved, ${s.skipped_count || 0} skipped, ${s.error_count || 0} errors.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Migration failed');
    } finally {
      setMigrating(false);
    }
  };

  const openBulk = async () => {
    setBulkOpen(true);
    setBulkResult(null);
    setBulkPreview(null);
    try {
      const res = await api.post(
        '/stripe/admin/promo-to-paid/bulk',
        { price_type: 'founding', send_email: false, dry_run: true },
        { headers: getAuthHeader() },
      );
      setBulkPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    }
  };

  const runBulk = async () => {
    setBulkRunning(true);
    try {
      const res = await api.post(
        '/stripe/admin/promo-to-paid/bulk',
        { price_type: bulkPriceType, send_email: bulkSendEmail, dry_run: false },
        { headers: getAuthHeader() },
      );
      setBulkResult(res.data);
      const s = res.data.summary || {};
      toast.success(`Bulk run complete — ${s.sent_count || 0} emails sent, ${s.checkout_count || 0} links generated, ${s.error_count || 0} errors.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk run failed');
    } finally {
      setBulkRunning(false);
    }
  };

  const pricesConfigured = config?.founding_price_id && config?.regular_price_id;

  return (
    <div
      className="bg-white rounded-2xl shadow-card p-5 border border-border-subtle"
      data-testid="stripe-pricing-setup-widget"
    >
      <div className="flex items-center gap-2 mb-3">
        {pricesConfigured
          ? <CheckCircle2 size={18} className="text-green-600" />
          : <AlertCircle size={18} className="text-amber-500" />}
        <h3 className="font-semibold text-navy text-sm">Stripe Pricing Setup</h3>
      </div>

      {loadingConfig ? (
        <p className="text-xs text-text-mid">Loading…</p>
      ) : (
        <>
          <p className="text-xs text-text-mid mb-3">
            {pricesConfigured
              ? <>Thrive prices configured ($24.99 Founding · $49.99 Regular). Founding window {config.founding_window_active ? 'active' : 'closed'}.</>
              : <>Click <strong>Create Thrive Prices</strong> once to set up Stripe pricing for the new 2-tier model.</>}
          </p>

          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={handleCreate}
              disabled={creating}
              className={`h-8 text-xs rounded-full ${pricesConfigured ? 'bg-gray-100 text-text-mid hover:bg-gray-200' : 'bg-teal text-white hover:bg-teal-deep'}`}
              data-testid="create-thrive-prices-btn"
            >
              {creating ? <Loader2 size={12} className="animate-spin mr-1" /> : <Wand2 size={12} className="mr-1" />}
              {pricesConfigured ? 'Recheck Prices' : 'Create Thrive Prices'}
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={runPreviewMigration}
              disabled={previewing || migrating || !pricesConfigured}
              className="h-8 text-xs rounded-full border-text-mid text-text-mid hover:bg-cream"
              data-testid="preview-migration-btn"
            >
              {previewing ? <Loader2 size={12} className="animate-spin mr-1" /> : null}
              Preview Migration (Dry Run)
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirmOpen(true)}
              disabled={migrating || !pricesConfigured}
              className="h-8 text-xs rounded-full border-teal text-teal hover:bg-teal hover:text-white"
              data-testid="migrate-legacy-subs-btn"
            >
              {migrating ? <Loader2 size={12} className="animate-spin mr-1" /> : <ArrowRightLeft size={12} className="mr-1" />}
              Migrate Legacy Subscribers
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={openBulk}
              disabled={bulkRunning || !pricesConfigured}
              className="h-8 text-xs rounded-full border-navy text-navy hover:bg-navy hover:text-white"
              data-testid="bulk-promo-to-paid-btn"
            >
              {bulkRunning ? <Loader2 size={12} className="animate-spin mr-1" /> : <Users size={12} className="mr-1" />}
              Bulk: Promo → $24.99
            </Button>
          </div>

          {migrationPreview && (
            <div className="mt-3 border border-dashed border-teal/40 rounded-xl p-3 bg-teal/5 text-xs space-y-2" data-testid="migration-preview-panel">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-navy">Dry-run preview (zero charges, zero writes)</p>
                <button
                  onClick={() => setMigrationPreview(null)}
                  className="text-text-light hover:text-text-mid text-[11px]"
                >
                  Dismiss
                </button>
              </div>
              <p className="text-text-mid">
                Would migrate <strong className="text-navy">{migrationPreview.summary?.would_migrate_count ?? 0}</strong>{' '}
                subscription{(migrationPreview.summary?.would_migrate_count ?? 0) === 1 ? '' : 's'} ·{' '}
                Skipped: <strong>{migrationPreview.summary?.skipped_count ?? 0}</strong> ·{' '}
                Errors: <strong className={migrationPreview.summary?.error_count ? 'text-red-600' : ''}>
                  {migrationPreview.summary?.error_count ?? 0}
                </strong>
              </p>

              {migrationPreview.would_migrate?.length > 0 && (
                <details open className="text-[11px]" data-testid="preview-would-migrate-list">
                  <summary className="cursor-pointer text-navy">Subscriptions that would migrate</summary>
                  <div className="mt-1 max-h-40 overflow-auto bg-white rounded border border-border-subtle">
                    <table className="w-full text-left">
                      <thead className="text-text-light text-[10px] bg-cream/50 sticky top-0">
                        <tr>
                          <th className="px-2 py-1 font-medium">Member</th>
                          <th className="px-2 py-1 font-medium">From</th>
                          <th className="px-2 py-1 font-medium">→ To</th>
                        </tr>
                      </thead>
                      <tbody>
                        {migrationPreview.would_migrate.map((row, i) => (
                          <tr key={i} className="border-t border-border-subtle">
                            <td className="px-2 py-1 truncate max-w-[160px]">{row.email}</td>
                            <td className="px-2 py-1 text-text-mid">${row.current_amount}/{row.current_interval}</td>
                            <td className="px-2 py-1 text-teal-deep">$24.99/mo</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}

              {migrationPreview.skipped?.length > 0 && (
                <details className="text-[11px]">
                  <summary className="cursor-pointer text-text-mid">
                    Skipped ({migrationPreview.skipped.length})
                  </summary>
                  <ul className="mt-1 pl-3 list-disc text-text-mid">
                    {migrationPreview.skipped.slice(0, 10).map((s, i) => (
                      <li key={i}>{s.email || '—'} ({s.reason})</li>
                    ))}
                  </ul>
                </details>
              )}

              {migrationPreview.summary?.would_migrate_count > 0 && (
                <Button
                  size="sm"
                  onClick={() => setConfirmOpen(true)}
                  className="h-8 text-xs rounded-full bg-teal hover:bg-teal-deep text-white w-full"
                  data-testid="preview-confirm-migrate-btn"
                >
                  Looks right — run the real migration
                </Button>
              )}
            </div>
          )}

          {lastMigration && (
            <div className="mt-3 text-xs space-y-1 text-text-mid border-t border-border-subtle pt-3" data-testid="migration-last-result">
              <p>
                <strong className="text-navy">{lastMigration.summary?.migrated_count ?? 0}</strong> migrated,{' '}
                <strong>{lastMigration.summary?.skipped_count ?? 0}</strong> skipped,{' '}
                <strong className={lastMigration.summary?.error_count ? 'text-red-600' : ''}>{lastMigration.summary?.error_count ?? 0}</strong> errors.
              </p>
              {lastMigration.errors?.length > 0 && (
                <details className="text-[11px]">
                  <summary className="cursor-pointer text-red-600">Show errors</summary>
                  <ul className="mt-1 pl-3 list-disc">
                    {lastMigration.errors.slice(0, 5).map((e, i) => (
                      <li key={i}>{e.email}: {e.error}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </>
      )}

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-md" data-testid="bulk-promo-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Bulk: move promo members to paid</DialogTitle>
            <DialogDescription>
              {bulkPreview
                ? <>This will target <strong>{bulkPreview.would_target_count}</strong> member{bulkPreview.would_target_count === 1 ? '' : 's'} currently on an active promo.</>
                : 'Loading preview…'}
            </DialogDescription>
          </DialogHeader>

          {!bulkResult ? (
            <div className="space-y-3">
              <div>
                <p className="text-xs font-medium text-text-mid mb-2">Rate</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setBulkPriceType('founding')}
                    className={`rounded-xl border-2 p-3 text-left transition-colors ${bulkPriceType === 'founding' ? 'border-teal bg-teal/10' : 'border-border-subtle hover:bg-cream/30'}`}
                    data-testid="bulk-select-founding"
                  >
                    <div className="text-sm font-semibold text-navy">$24.99 / mo</div>
                    <div className="text-[11px] text-text-mid">Founding rate</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBulkPriceType('regular')}
                    className={`rounded-xl border-2 p-3 text-left transition-colors ${bulkPriceType === 'regular' ? 'border-teal bg-teal/10' : 'border-border-subtle hover:bg-cream/30'}`}
                    data-testid="bulk-select-regular"
                  >
                    <div className="text-sm font-semibold text-navy">$49.99 / mo</div>
                    <div className="text-[11px] text-text-mid">Regular rate</div>
                  </button>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-text-mid cursor-pointer">
                <input
                  type="checkbox"
                  checked={bulkSendEmail}
                  onChange={(e) => setBulkSendEmail(e.target.checked)}
                  data-testid="bulk-send-email-checkbox"
                />
                Email upgrade link to each member
              </label>
              {bulkPreview?.preview_emails?.length > 0 && (
                <details className="text-[11px] text-text-mid">
                  <summary className="cursor-pointer">First {bulkPreview.preview_emails.length} target email{bulkPreview.preview_emails.length === 1 ? '' : 's'}</summary>
                  <ul className="mt-1 pl-3 list-disc">
                    {bulkPreview.preview_emails.map((e) => <li key={e}>{e}</li>)}
                  </ul>
                </details>
              )}
            </div>
          ) : (
            <div className="bg-teal/5 border border-teal/20 rounded-2xl p-4 text-sm" data-testid="bulk-result">
              <p className="font-semibold text-navy mb-2">Done.</p>
              <p>Targeted: <strong>{bulkResult.summary?.targeted}</strong></p>
              <p>Checkout links generated: <strong>{bulkResult.summary?.checkout_count}</strong></p>
              <p>Emails sent: <strong>{bulkResult.summary?.sent_count}</strong></p>
              <p className={bulkResult.summary?.error_count ? 'text-red-600' : ''}>
                Errors: <strong>{bulkResult.summary?.error_count}</strong>
              </p>
              {bulkResult.errors?.length > 0 && (
                <details className="mt-2 text-[11px]">
                  <summary className="cursor-pointer">Show error details</summary>
                  <ul className="mt-1 pl-3 list-disc">
                    {bulkResult.errors.slice(0, 10).map((e, i) => (
                      <li key={i}>{e.email}: {e.error}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          <DialogFooter>
            {!bulkResult ? (
              <>
                <Button variant="ghost" onClick={() => setBulkOpen(false)}>Cancel</Button>
                <Button
                  onClick={runBulk}
                  disabled={bulkRunning || !bulkPreview || bulkPreview.would_target_count === 0}
                  className="bg-teal hover:bg-teal-deep text-white rounded-full"
                  data-testid="bulk-confirm-btn"
                >
                  {bulkRunning && <Loader2 size={14} className="animate-spin mr-1" />}
                  Run on {bulkPreview?.would_target_count ?? '…'} member{bulkPreview?.would_target_count === 1 ? '' : 's'}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => { setBulkOpen(false); setBulkResult(null); }}
                className="bg-teal hover:bg-teal-deep text-white rounded-full"
              >
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="migrate-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Migrate legacy subscribers?</DialogTitle>
            <DialogDescription className="text-text-mid leading-relaxed">
              This will migrate all legacy Connect subscribers to the Thrive founding member rate of
              <strong> $24.99/month</strong>. <strong>No charges will be made immediately.</strong> The new price
              takes effect on each member&apos;s next scheduled renewal date. Their billing cycle date will not
              change. <span className="text-red-700 font-semibold">This action cannot be undone</span> — confirm
              you want to proceed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button
              onClick={runMigration}
              className="bg-teal hover:bg-teal-deep text-white rounded-full"
              data-testid="confirm-migration-btn"
            >
              Yes, migrate now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
